import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  List,
  ListItemButton,
  Divider,
  Chip,
  Button,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  Stack,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { MainLayout } from '../../components/Layout/MainLayout';
import { useNotifications } from '../../hooks/useNotifications';
import { useNotificationContext } from '../../hooks/NotificationContext';
import { eventNotificationService } from '../../services/eventNotificationService';
import { NotificationData, NOTIFICATION_TEMPLATES, getNotificationDisplayName } from '../../types/notification';
import { NotificationDetailDialog } from '../../components/Admin/NotificationDetailDialog';

type TabType = 'student' | 'company' | 'handled';

const formatDatetime = (iso: string): string => {
  const d = new Date(iso);
  const y = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const h = String(d.getHours()).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');
  return `${y}/${mo}/${day} ${h}:${mi}`;
};

const AdminNotificationsPage: React.FC = () => {
  const {
    notifications,
    total,
    isLoading,
    isLoadingMore,
    error,
    hasMore,
    fetch,
    loadMore,
    optimisticMarkAsRead,
    optimisticMarkAllAsRead,
    optimisticDelete,
    optimisticDeleteRead,
    optimisticToggleHandled,
    optimisticDeleteHandled,
  } = useNotifications();

  const { unreadCount: contextUnreadCount, decrementUnread, resetUnread, refetch } = useNotificationContext();

  const [activeTab, setActiveTab] = useState<TabType>('student');

  const [selectedNotification, setSelectedNotification] = useState<NotificationData | null>(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);

  const [markAllDialogOpen, setMarkAllDialogOpen] = useState(false);
  const [isMarkingAll, setIsMarkingAll] = useState(false);

  const [deleteTargetId, setDeleteTargetId] = useState<number | null>(null);
  const [isDeletingOne, setIsDeletingOne] = useState(false);
  const [deleteReadDialogOpen, setDeleteReadDialogOpen] = useState(false);
  const [isDeletingRead, setIsDeletingRead] = useState(false);
  const [deleteHandledDialogOpen, setDeleteHandledDialogOpen] = useState(false);
  const [isDeletingHandled, setIsDeletingHandled] = useState(false);

  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false, message: '', severity: 'success',
  });

  // 初回マウント時は学生タブを取得。タブ切替時は handleTabChange 経由で再取得。
  useEffect(() => {
    fetch('student');
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // カテゴリ別 API を使用しているため、クライアントサイドの絞り込みは不要
  const visibleNotifications = notifications;
  const unreadCount          = contextUnreadCount;

  const handleItemClick = async (ntf: NotificationData) => {
    setSelectedNotification(ntf);
    setDetailDialogOpen(true);

    if (!ntf.is_read) {
      const rollback = optimisticMarkAsRead(ntf.id);
      decrementUnread();
      try {
        await eventNotificationService.markAsRead(ntf.id);
      } catch {
        rollback();
        refetch();
        setSnackbar({ open: true, message: '既読の更新に失敗しました', severity: 'error' });
      }
    }
  };

  const handleDetailClose = () => {
    setDetailDialogOpen(false);
  };

  const handleToggleHandled = async (ntf: NotificationData) => {
    const newHandled = !ntf.is_handled;
    const rollback = optimisticToggleHandled(ntf.id);
    if (newHandled && !ntf.is_read) decrementUnread();
    try {
      await eventNotificationService.toggleHandled(ntf.id);
      // 対応済みにした場合は詳細ダイアログを閉じてトーストを表示
      if (newHandled) {
        setDetailDialogOpen(false);
        setSnackbar({ open: true, message: '対応済みにしました', severity: 'success' });
      } else {
        setSnackbar({ open: true, message: '未対応に戻しました', severity: 'success' });
      }
    } catch {
      rollback();
      refetch();
      setSnackbar({ open: true, message: '対応済みの更新に失敗しました', severity: 'error' });
    }
  };

  const handleMarkAllConfirm = async () => {
    setMarkAllDialogOpen(false);
    setIsMarkingAll(true);
    const rollback = optimisticMarkAllAsRead();
    resetUnread();
    try {
      await eventNotificationService.markAllAsRead();
      setSnackbar({ open: true, message: 'すべての通知を既読にしました', severity: 'success' });
    } catch {
      rollback();
      refetch();
      setSnackbar({ open: true, message: '一括既読の更新に失敗しました', severity: 'error' });
    } finally {
      setIsMarkingAll(false);
    }
  };

  const handleDeleteOne = async () => {
    if (deleteTargetId === null) return;
    setIsDeletingOne(true);
    const id = deleteTargetId;
    setDeleteTargetId(null);
    const rollback = optimisticDelete(id);
    try {
      await eventNotificationService.deleteNotification(id);
      setSnackbar({ open: true, message: '通知を削除しました', severity: 'success' });
    } catch {
      rollback();
      setSnackbar({ open: true, message: '削除に失敗しました', severity: 'error' });
    } finally {
      setIsDeletingOne(false);
    }
  };

  const handleDeleteReadConfirm = async () => {
    setDeleteReadDialogOpen(false);
    setIsDeletingRead(true);
    const rollback = optimisticDeleteRead();
    try {
      const { deleted_count } = await eventNotificationService.deleteReadNotifications();
      setSnackbar({ open: true, message: `既読の通知を${deleted_count}件削除しました`, severity: 'success' });
    } catch {
      rollback();
      setSnackbar({ open: true, message: '削除に失敗しました', severity: 'error' });
    } finally {
      setIsDeletingRead(false);
    }
  };

  const handleDeleteHandledConfirm = async () => {
    setDeleteHandledDialogOpen(false);
    setIsDeletingHandled(true);
    const rollback = optimisticDeleteHandled();
    try {
      const { deleted_count } = await eventNotificationService.deleteHandledNotifications();
      setSnackbar({ open: true, message: `対応済みの通知を${deleted_count}件削除しました`, severity: 'success' });
    } catch {
      rollback();
      setSnackbar({ open: true, message: '削除に失敗しました', severity: 'error' });
    } finally {
      setIsDeletingHandled(false);
    }
  };

  const hasReadInUnhandled = activeTab !== 'handled' && notifications.some((n) => n.is_read);

  return (
    <MainLayout>
      <Container maxWidth="md" sx={{ py: 3 }}>
        {/* ヘッダー */}
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          alignItems={{ xs: 'flex-start', md: 'center' }}
          justifyContent="space-between"
          spacing={{ xs: 1, md: 0 }}
          mb={2}
        >
          {/* 左グループ */}
          <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap">
            <Typography variant="h5" fontWeight={700}>
              通知一覧
            </Typography>
            {unreadCount > 0 && (
              <Chip
                label={`未読: ${unreadCount}件`}
                size="small"
                color="error"
                variant="filled"
              />
            )}
            {unreadCount > 0 && (
              <Button
                variant="text"
                onClick={() => setMarkAllDialogOpen(true)}
                disabled={isMarkingAll}
                startIcon={isMarkingAll ? <CircularProgress size={14} color="inherit" /> : undefined}
                sx={{ px: 0 }}
              >
                {isMarkingAll ? '処理中...' : 'すべて既読にする'}
              </Button>
            )}
          </Stack>

          {/* 右グループ：タブに応じて削除ボタンを切替 */}
          {activeTab !== 'handled' && hasReadInUnhandled && (
            <Button
              variant="text"
              color="error"
              onClick={() => setDeleteReadDialogOpen(true)}
              disabled={isDeletingRead}
              startIcon={isDeletingRead ? <CircularProgress size={14} color="inherit" /> : <DeleteOutlineIcon fontSize="small" />}
              sx={{ px: 0, alignSelf: { xs: 'flex-end', md: 'center' } }}
            >
              {isDeletingRead ? '削除中...' : '既読を削除'}
            </Button>
          )}
          {activeTab === 'handled' && notifications.length > 0 && (
            <Button
              variant="text"
              color="error"
              onClick={() => setDeleteHandledDialogOpen(true)}
              disabled={isDeletingHandled}
              startIcon={isDeletingHandled ? <CircularProgress size={14} color="inherit" /> : <DeleteOutlineIcon fontSize="small" />}
              sx={{ px: 0, alignSelf: { xs: 'flex-end', md: 'center' } }}
            >
              {isDeletingHandled ? '削除中...' : '対応済みを削除'}
            </Button>
          )}
        </Stack>

        {/* タブ */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 0 }}>
          <Tabs
            value={activeTab}
            onChange={(_, v) => {
              const tab = v as TabType;
              setActiveTab(tab);
              fetch(tab);
            }}
            sx={{ minHeight: 40 }}
          >
            <Tab label="学生" value="student" sx={{ minHeight: 40, fontSize: '0.85rem', fontWeight: 600 }} />
            <Tab label="企業" value="company" sx={{ minHeight: 40, fontSize: '0.85rem', fontWeight: 600 }} />
            <Tab label="対応済み" value="handled" sx={{ minHeight: 40, fontSize: '0.85rem', fontWeight: 600 }} />
          </Tabs>
        </Box>

        {/* 通知リスト */}
        <Paper elevation={2}>
          {isLoading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Box p={3}>
              <Alert severity="error">{error}</Alert>
            </Box>
          ) : visibleNotifications.length === 0 ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
              <Typography color="text.secondary">通知はありません</Typography>
            </Box>
          ) : (
            <>
              <List disablePadding>
                {visibleNotifications.map((ntf, idx) => (
                  <React.Fragment key={ntf.id}>
                    <ListItemButton
                      onClick={() => handleItemClick(ntf)}
                      sx={{
                        py: 1.5,
                        px: 2.5,
                        alignItems: 'flex-start',
                        cursor: 'pointer',
                        '&:hover': {
                          '& .delete-btn': { opacity: 1 },
                        },
                      }}
                    >
                      {/* 未読ドット（対応済みタブでは非表示） */}
                      <Box
                        sx={{
                          width:           10,
                          height:          10,
                          borderRadius:    '50%',
                          backgroundColor: (!ntf.is_handled && !ntf.is_read) ? '#1976D2' : 'transparent',
                          flexShrink:      0,
                          mr:              1.5,
                          mt:              0.75,
                        }}
                      />

                      <Box flex={1} minWidth={0}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: 'block', mb: 0.5 }}
                        >
                          {formatDatetime(ntf.created_at)}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color:           (ntf.is_read || ntf.is_handled) ? '#9E9E9E' : 'text.primary',
                            overflow:        'hidden',
                            display:         '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            wordBreak:       'break-word',
                          }}
                        >
                          {NOTIFICATION_TEMPLATES[ntf.type](getNotificationDisplayName(ntf), ntf.event.title)}
                        </Typography>
                      </Box>

                      {/* 削除ボタン */}
                      <Tooltip title="削除">
                        <IconButton
                          className="delete-btn"
                          size="small"
                          onClick={(e) => { e.stopPropagation(); setDeleteTargetId(ntf.id); }}
                          sx={{
                            opacity:    { xs: 1, md: 0 },
                            transition: 'opacity 0.15s',
                            flexShrink: 0,
                            alignSelf:  'center',
                            ml:         1,
                            color:      'text.secondary',
                            '&:hover':  { color: 'error.main' },
                          }}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </ListItemButton>
                    {idx < visibleNotifications.length - 1 && <Divider component="li" />}
                  </React.Fragment>
                ))}
              </List>

              {/* さらに読み込む（全件ロードが必要な場合） */}
              {hasMore && (
                <Box display="flex" flexDirection="column" alignItems="center" py={2}>
                  <Button
                    variant="text"
                    onClick={loadMore}
                    disabled={isLoadingMore}
                  >
                    さらに読み込む
                  </Button>
                  {isLoadingMore && <CircularProgress size={20} sx={{ mt: 1 }} />}
                </Box>
              )}
            </>
          )}
        </Paper>
      </Container>

      {/* 申込詳細ダイアログ */}
      <NotificationDetailDialog
        open={detailDialogOpen}
        onClose={handleDetailClose}
        notification={selectedNotification}
        onToggleHandled={handleToggleHandled}
      />

      {/* 個別削除 確認ダイアログ */}
      <Dialog open={deleteTargetId !== null} onClose={() => setDeleteTargetId(null)} maxWidth="xs" fullWidth>
        <DialogTitle>通知を削除しますか？</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            この通知を削除します。この操作は元に戻せません。
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTargetId(null)}>キャンセル</Button>
          <Button variant="contained" color="error" onClick={handleDeleteOne} disabled={isDeletingOne}>
            削除する
          </Button>
        </DialogActions>
      </Dialog>

      {/* 既読一括削除 確認ダイアログ */}
      <Dialog open={deleteReadDialogOpen} onClose={() => setDeleteReadDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>既読の通知を削除しますか？</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            未対応の既読通知（{notifications.filter((n) => n.is_read).length}件）をすべて削除します。この操作は元に戻せません。
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteReadDialogOpen(false)}>キャンセル</Button>
          <Button variant="contained" color="error" onClick={handleDeleteReadConfirm}>
            削除する
          </Button>
        </DialogActions>
      </Dialog>

      {/* 対応済み一括削除 確認ダイアログ */}
      <Dialog open={deleteHandledDialogOpen} onClose={() => setDeleteHandledDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>対応済みの通知を削除しますか？</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            対応済みの通知（{notifications.length}件）をすべて削除します。この操作は元に戻せません。
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteHandledDialogOpen(false)}>キャンセル</Button>
          <Button variant="contained" color="error" onClick={handleDeleteHandledConfirm}>
            削除する
          </Button>
        </DialogActions>
      </Dialog>

      {/* 一括既読 確認ダイアログ */}
      <Dialog open={markAllDialogOpen} onClose={() => setMarkAllDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>すべて既読にしますか？</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            未読の通知（{unreadCount}件）をすべて既読にします。この操作は元に戻せません。
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMarkAllDialogOpen(false)}>キャンセル</Button>
          <Button variant="contained" onClick={handleMarkAllConfirm}>
            既読にする
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </MainLayout>
  );
};

export default AdminNotificationsPage;
